// DEPENDENCIES ---------------------------------------------------------------- //

import { useMemo, useState } from 'react';

import { gql, useQuery } from '@apollo/client';

import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { MdFileDownload } from 'react-icons/md';
import { toast } from 'react-toastify';

import { getMonetaryValue } from '@services/formatService';

import { useUser } from '@store';
import {
  PROGRAM_REPORT_VISIBLE_STATUS,
  ProgramReportRow,
  canViewProgramReports,
  formatGeneratedAt,
  formatReportPeriod,
  programReportErrorMessage,
  shouldShowProgramColumn,
} from '@features/programs/programReports';
import { fetchProgramReportUrl } from '@features/programs/services/programReportsService';

// QUERIES ---------------------------------------------------------------- //
// No program filter in the where clause: `program_reports` select for the
// `program-admin` role is already filtered server-side to programs the caller
// has a `usertoprograms` row for. The only client-side narrowing is status.

const GET_PROGRAM_REPORTS = gql`
  query getProgramReports($status: String!) {
    program_reports(where: { status: { _eq: $status } }, order_by: { period_end: desc }) {
      id
      program_id
      report_type
      period_start
      period_end
      status
      row_count
      claim_count
      total_program_amount
      generated_at
      generated_by
      source
      created_at
      program {
        id
        name
      }
    }
  }
`;

// HELPERS ---------------------------------------------------------------- //

const ROWS_PER_PAGE = 10;

const countOf = (value?: number | null): string => (value == null ? '—' : String(value));

// COMPONENT ---------------------------------------------------------------- //

/**
 * The program admin's semi-monthly detail reports — list and download only.
 * Generating is submitting, so the generate path stays in admin-v5; nothing
 * here can change claim state.
 *
 * Renders nothing at all for any other role.
 */
const ProgramReportsSection = () => {
  const theme = useTheme();
  const user = useUser();

  const isProgramAdmin = canViewProgramReports(user);

  const [page, setPage] = useState(0);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { data, loading } = useQuery(GET_PROGRAM_REPORTS, {
    variables: { status: PROGRAM_REPORT_VISIBLE_STATUS },
    skip: !isProgramAdmin,
    fetchPolicy: 'cache-and-network',
  });

  const reports: ProgramReportRow[] = useMemo(() => data?.program_reports ?? [], [data]);
  const showProgram = useMemo(() => shouldShowProgramColumn(reports), [reports]);
  const pageRows = useMemo(
    () => reports.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE),
    [reports, page],
  );

  const onDownload = async (report: ProgramReportRow) => {
    setDownloadingId(report.id);
    try {
      const token = await user?.getToken();
      const url = await fetchProgramReportUrl(report.id, token);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error(`Failed to get the download URL for program report ${report.id}`, error);
      toast.error(programReportErrorMessage(error), { toastId: `program-report-download` });
    } finally {
      setDownloadingId(null);
    }
  };

  if (!isProgramAdmin) return null;

  return (
    <Box>
      {/* No heading: this renders inside the Reports tab, whose label already names it. */}
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Program move detail for each completed reporting period. Download links expire after an hour.
      </Typography>

      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

      {/* An empty outlined Paper reads as "none", so hold it back until the first load settles. */}
      <Paper variant='outlined' sx={{ overflow: 'hidden', display: loading && reports.length === 0 ? 'none' : 'block' }}>
        {reports.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant='body2' color='text.secondary'>
              No reports yet. Each reporting period's detail file appears here once it has been generated.
            </Typography>
          </Box>
        ) : (
          <>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ '& th': { ...HEAD_CELL_SX, bgcolor: theme.palette.action.hover } }}>
                  {showProgram ? <TableCell>Program</TableCell> : null}
                  <TableCell>Period</TableCell>
                  <TableCell align='right'>Moves</TableCell>
                  <TableCell align='right'>Claims</TableCell>
                  <TableCell align='right'>Program amount</TableCell>
                  <TableCell>Generated</TableCell>
                  <TableCell align='right'>Download</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map(report => (
                  <TableRow key={report.id} hover>
                    {showProgram ? (
                      <TableCell sx={{ fontWeight: 500 }}>{report.program?.name ?? `Program ${report.program_id}`}</TableCell>
                    ) : null}
                    <TableCell>{formatReportPeriod(report.period_start, report.period_end)}</TableCell>
                    <TableCell align='right' sx={NUMERIC_CELL_SX}>
                      {countOf(report.row_count)}
                    </TableCell>
                    <TableCell align='right' sx={NUMERIC_CELL_SX}>
                      {countOf(report.claim_count)}
                    </TableCell>
                    <TableCell align='right' sx={NUMERIC_CELL_SX}>
                      {getMonetaryValue(Number(report.total_program_amount ?? 0))}
                    </TableCell>
                    <TableCell>{formatGeneratedAt(report.generated_at)}</TableCell>
                    <TableCell align='right'>
                      {downloadingId === report.id ? (
                        <Box sx={{ display: 'inline-flex', p: 1 }}>
                          <CircularProgress size={18} />
                        </Box>
                      ) : (
                        <Tooltip title='Download report'>
                          {/* Span wrapper: a disabled button fires no events, so the tooltip needs one. */}
                          <span>
                            <IconButton
                              size='small'
                              aria-label={`Download the ${formatReportPeriod(report.period_start, report.period_end)} report`}
                              disabled={downloadingId !== null}
                              onClick={() => onDownload(report)}
                            >
                              <MdFileDownload size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {reports.length > ROWS_PER_PAGE ? (
              <TablePagination
                component='div'
                count={reports.length}
                page={page}
                onPageChange={(_event, next) => setPage(next)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
              />
            ) : null}
          </>
        )}
      </Paper>
    </Box>
  );
};

const HEAD_CELL_SX = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
} as const;

const NUMERIC_CELL_SX = { fontWeight: 600 } as const;

// EXPORT ---------------------------------------------------------------- //

export default ProgramReportsSection;
